import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api, setActiveLocationId } from '../lib/api';
import { DEFAULT_CURRENCY, formatMoney, formatTotals } from '../lib/currency';

/**
 * LocationContext — the source of truth for the merchant's active location.
 *
 * A tenant can register multiple UnTill locations. Every data-fetching call
 * needs to know which location to filter by:
 *   - `currentLocationId === null`  → "All Locations" (combined view)
 *   - `currentLocationId === <uuid>` → a specific location
 *
 * The selection is persisted per user in localStorage so it survives reloads.
 */
const LocationContext = createContext(null);

export const useTenantLocation = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useTenantLocation must be used within LocationProvider');
  return ctx;
};

const storageKey = (userId) => `syncpoint.activeLocation.${userId || 'anon'}`;

export const LocationProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentLocationId, _setCurrentLocationId] = useState(null); // null = "all"

  const load = useCallback(async () => {
    if (!token) { setLocations([]); return; }
    setLoading(true);
    try {
      const r = await api.listLocations(token);
      const list = r.data.locations || [];
      setLocations(list);
      // Reconcile persisted choice with what the tenant actually owns
      const persisted = localStorage.getItem(storageKey(user?.id));
      let next;
      if (persisted === 'all') {
        next = null;
      } else if (persisted && list.some((l) => l.location_id === persisted)) {
        next = persisted;
      } else {
        // Default to "All Locations" when the tenant has 2+ locations,
        // or to the single location when they only have one.
        next = list.length === 1 ? list[0].location_id : null;
      }
      _setCurrentLocationId(next);
      setActiveLocationId(next);
    } catch {
      setLocations([]);
    } finally { setLoading(false); }
  }, [token, user?.id]);

  useEffect(() => { load(); }, [load]);

  const setCurrentLocationId = (id) => {
    _setCurrentLocationId(id);
    setActiveLocationId(id);
    localStorage.setItem(storageKey(user?.id), id || 'all');
  };

  const currentLocation = currentLocationId
    ? locations.find((l) => l.location_id === currentLocationId)
    : null;

  const defaultCurrency = (locations.find((l) => l.is_default) || locations[0])?.currency || DEFAULT_CURRENCY;
  const currencyFor = useCallback((locationId) => {
    const id = locationId || currentLocationId;
    return locations.find((l) => l.location_id === id)?.currency || defaultCurrency;
  }, [locations, currentLocationId, defaultCurrency]);

  return (
    <LocationContext.Provider value={{
      locations, loading, currentLocationId, currentLocation,
      setCurrentLocationId, refresh: load, currencyFor,
    }}>
      {children}
    </LocationContext.Provider>
  );
};

/**
 * Money helpers bound to each location's currency.
 * money(amount, locationId?) formats one value; totals(rows, amountOf) sums per currency.
 */
export const useMoney = () => {
  const { currencyFor } = useTenantLocation();
  return {
    currencyFor,
    money: (amount, locationId) => formatMoney(amount, currencyFor(locationId)),
    totals: (rows, amountOf) => formatTotals(rows, amountOf, (r) => currencyFor(r.location_id)),
  };
};

/**
 * Merge the active location_id into an existing axios `params` object.
 * Use in every data-fetching call so the backend can filter correctly.
 * Never sends the param when the value is null (== "All Locations").
 */
export const withLocation = (params, currentLocationId) => {
  if (!currentLocationId) return params;
  return { ...(params || {}), location_id: currentLocationId };
};
